// ============================================================
// VendorsTab.jsx  —  Noble Enterprises (tape module)
//
// WHAT'S ON THIS SCREEN
//   • Vendor list with a live balance per vendor
//   • Add / edit form (inline, not a modal)
//   • Payment modal    — record money paid to a vendor
//   • Ledger modal     — statement with running balance
//
// The balance is NOT stored anywhere. The backend derives it
// from purchases minus payments every time it's read, so it
// can never fall out of step with the transactions.
//
// HOW THIS FILE IS ORGANISED
//   A. Shared UI bits
//   B. ComboField    — dropdown that can create a new option
//   C. VendorForm
//   D. PaymentModal
//   E. LedgerModal
//   F. VendorsTab    — the wrapper
// ============================================================

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import WindowModal, { DEFAULT_FROM_DATE } from '../components/WindowModal';
import { Plus, Pencil, Trash2, X, Check, Wallet, FileText } from 'lucide-react';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const C = {
  brand:      '#f39c12',
  text:       '#1a2332',
  textSoft:   '#64748b',
  textMuted:  '#94a3b8',
  border:     '#e2e8f0',
  borderSoft: '#f1f5f9',
  card:       '#ffffff',
  rowAlt:     '#fafbfc',
  danger:     '#dc2626',
  dangerBg:   '#fee2e2',
  success:    '#059669',
  successBg:  '#d1fae5',
  info:       '#0284c7',
};

const money = (n) => `Rs ${Number(n || 0).toLocaleString('en-PK', { maximumFractionDigits: 0 })}`;
const today = () => new Date().toISOString().split('T')[0];


// ============================================================
// A. SHARED UI BITS
// ============================================================

const inputStyle = {
  width: '100%', padding: '10px 13px',
  border: `1px solid ${C.border}`, borderRadius: '8px',
  fontSize: '14px', color: C.text, boxSizing: 'border-box',
  fontFamily: 'inherit', background: '#ffffff',
};

const Field = ({ label, children }) => (
  <div>
    <label style={{
      display: 'block', fontSize: '12px', fontWeight: '600',
      color: C.textSoft, marginBottom: '6px',
    }}>
      {label}
    </label>
    {children}
  </div>
);

const TextInput = (props) => <input style={inputStyle} {...props} />;

const Btn = ({ variant = 'primary', children, ...props }) => {
  const styles = {
    primary: { background: C.brand,   color: '#ffffff',  border: 'none' },
    ghost:   { background: '#ffffff', color: C.textSoft, border: `1px solid ${C.border}` },
  };
  return (
    <button
      style={{
        ...styles[variant],
        padding: '10px 18px', borderRadius: '8px', fontSize: '14px',
        fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit',
        display: 'inline-flex', alignItems: 'center', gap: '7px',
      }}
      {...props}
    >
      {children}
    </button>
  );
};

const IconBtn = ({ danger, title, children, ...props }) => (
  <button
    title={title}
    style={{
      width: '32px', height: '32px', borderRadius: '7px',
      border: `1px solid ${C.border}`, background: '#ffffff',
      color: danger ? C.danger : C.textSoft, cursor: 'pointer',
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    }}
    {...props}
  >
    {children}
  </button>
);

const StatusPill = ({ active }) => (
  <span style={{
    fontSize: '12px', fontWeight: '600', padding: '3px 10px', borderRadius: '11px',
    background: active ? C.successBg : C.borderSoft,
    color:      active ? C.success   : C.textMuted,
  }}>
    {active ? 'Active' : 'Inactive'}
  </span>
);

const Toggle = ({ checked, onChange, label }) => (
  <button
    type="button"
    onClick={() => onChange(!checked)}
    style={{
      display: 'flex', alignItems: 'center', gap: '9px',
      background: 'none', border: 'none', cursor: 'pointer',
      padding: 0, fontFamily: 'inherit',
    }}
  >
    <span style={{
      width: '38px', height: '22px', borderRadius: '11px',
      background: checked ? C.brand : C.border,
      position: 'relative', transition: 'background 0.15s', flexShrink: 0,
    }}>
      <span style={{
        position: 'absolute', top: '3px', left: checked ? '19px' : '3px',
        width: '16px', height: '16px', borderRadius: '50%',
        background: '#ffffff', transition: 'left 0.15s',
      }} />
    </span>
    <span style={{ fontSize: '14px', color: C.text }}>{label}</span>
  </button>
);

const Banner = ({ type, message, onClose }) => {
  if (!message) return null;
  const isError = type === 'error';
  return (
    <div style={{
      background: isError ? C.dangerBg : C.successBg,
      color:      isError ? C.danger   : C.success,
      padding: '11px 16px', borderRadius: '8px', marginBottom: '16px',
      fontSize: '14px', display: 'flex', alignItems: 'center', gap: '10px',
    }}>
      <span style={{ flex: 1 }}>{message}</span>
      <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', display: 'flex' }}>
        <X size={16} />
      </button>
    </div>
  );
};

// Generic modal shell
const Modal = ({ title, subtitle, onClose, width = '520px', children }) => (
  <div
    onClick={onClose}
    style={{
      position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, padding: '20px',
    }}
  >
    <div
      onClick={e => e.stopPropagation()}
      style={{
        background: '#ffffff', borderRadius: '14px', width, maxWidth: '100%',
        maxHeight: '88vh', display: 'flex', flexDirection: 'column',
        boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
      }}
    >
      <div style={{
        padding: '20px 24px', borderBottom: `1px solid ${C.borderSoft}`,
        display: 'flex', alignItems: 'flex-start', gap: '14px',
      }}>
        <div style={{ flex: 1 }}>
          <h3 style={{ margin: 0, fontSize: '17px', fontWeight: '700', color: C.text }}>{title}</h3>
          {subtitle && (
            <p style={{ margin: '3px 0 0', fontSize: '13px', color: C.textSoft }}>{subtitle}</p>
          )}
        </div>
        <button
          onClick={onClose}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textMuted, display: 'flex', padding: '2px' }}
        >
          <X size={20} />
        </button>
      </div>
      <div style={{ padding: '22px 24px', overflowY: 'auto' }}>{children}</div>
    </div>
  </div>
);

const thStyle = {
  textAlign: 'left', padding: '11px 22px', fontSize: '12px',
  fontWeight: '600', color: C.textSoft, background: C.rowAlt,
  textTransform: 'uppercase', letterSpacing: '0.3px',
};
const tdStyle = {
  padding: '13px 22px', fontSize: '14px', color: C.text,
  borderTop: `1px solid ${C.borderSoft}`,
};


// ============================================================
// B. ComboField — dropdown that can flip to "type a new name"
// ============================================================

const ComboField = ({ label, options, value, newName, isNew, onChange, placeholder }) => (
  <Field label={label}>
    <div style={{ display: 'flex', gap: '8px' }}>
      {isNew ? (
        <TextInput
          autoFocus
          value={newName}
          onChange={e => onChange({ value: '', newName: e.target.value, isNew: true })}
          placeholder={placeholder}
        />
      ) : (
        <select
          style={inputStyle}
          value={value}
          onChange={e => onChange({ value: e.target.value, newName: '', isNew: false })}
        >
          <option value="">Select…</option>
          {options.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
        </select>
      )}
      <button
        type="button"
        onClick={() => onChange({ value: '', newName: '', isNew: !isNew })}
        style={{
          flexShrink: 0, padding: '0 13px', borderRadius: '8px',
          border: `1px solid ${isNew ? C.brand : C.border}`,
          background: isNew ? C.brand : '#ffffff',
          color: isNew ? '#ffffff' : C.textSoft,
          fontSize: '13px', fontWeight: '600', cursor: 'pointer',
          fontFamily: 'inherit', whiteSpace: 'nowrap',
        }}
      >
        {isNew ? 'Cancel' : '+ New'}
      </button>
    </div>
  </Field>
);


// ============================================================
// C. VENDOR FORM
// ============================================================

const emptyVendor = {
  id: null,
  name: '',
  vendor_type_id: '', newType: '', isNewType: false,
  contact_person: '', phone: '', email: '', address: '',
  ntn: '', strn: '', payment_terms_days: 30,
  is_active: true,
};

const VendorForm = ({ form, setForm, vendorTypes, onSave, onCancel, saving }) => (
  <div style={{ padding: '20px 22px', background: C.rowAlt, borderBottom: `1px solid ${C.borderSoft}` }}>
    <div style={{
      display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
      gap: '16px', marginBottom: '18px',
    }}>
      <Field label="Vendor name">
        <TextInput
          value={form.name}
          onChange={e => setForm({ ...form, name: e.target.value })}
          placeholder="e.g. Ali Traders"
        />
      </Field>

      <ComboField
        label="Vendor type"
        options={vendorTypes}
        value={form.vendor_type_id}
        newName={form.newType}
        isNew={form.isNewType}
        placeholder="e.g. Ink supplier"
        onChange={({ value, newName, isNew }) => setForm({
          ...form, vendor_type_id: value, newType: newName, isNewType: isNew,
        })}
      />

      <Field label="Contact person">
        <TextInput
          value={form.contact_person}
          onChange={e => setForm({ ...form, contact_person: e.target.value })}
          placeholder="Optional"
        />
      </Field>

      <Field label="Phone">
        <TextInput
          value={form.phone}
          onChange={e => setForm({ ...form, phone: e.target.value })}
          placeholder="0300-1234567"
        />
      </Field>

      <Field label="Email">
        <TextInput
          type="email"
          value={form.email}
          onChange={e => setForm({ ...form, email: e.target.value })}
          placeholder="Optional"
        />
      </Field>

      <Field label="Address">
        <TextInput
          value={form.address}
          onChange={e => setForm({ ...form, address: e.target.value })}
          placeholder="Optional"
        />
      </Field>

      <Field label="NTN">
        <TextInput
          value={form.ntn}
          onChange={e => setForm({ ...form, ntn: e.target.value })}
          placeholder="Optional"
        />
      </Field>

      <Field label="STRN">
        <TextInput
          value={form.strn}
          onChange={e => setForm({ ...form, strn: e.target.value })}
          placeholder="Optional"
        />
      </Field>

      <Field label="Payment terms (days)">
        <TextInput
          type="number"
          value={form.payment_terms_days}
          onChange={e => setForm({ ...form, payment_terms_days: e.target.value })}
          placeholder="30"
        />
      </Field>

      {form.id && (
        <Field label="Status">
          <div style={{ paddingTop: '9px' }}>
            <Toggle
              checked={form.is_active}
              onChange={v => setForm({ ...form, is_active: v })}
              label={form.is_active ? 'Active' : 'Inactive'}
            />
          </div>
        </Field>
      )}
    </div>

    <div style={{ display: 'flex', gap: '10px' }}>
      <Btn onClick={onSave} disabled={saving}>
        <Check size={16} /> {saving ? 'Saving…' : 'Save vendor'}
      </Btn>
      <Btn variant="ghost" onClick={onCancel}>Cancel</Btn>
    </div>
  </div>
);


// ============================================================
// D. PAYMENT MODAL
// ============================================================

const PaymentModal = ({ vendor, onClose, onSaved, notify }) => {
  const [form, setForm] = useState({
    payment_date: today(), ref_no: '', against_no: '',
    amount: '', wht_amount: '', method: 'cash', notes: '',
  });
  const [saving, setSaving] = useState(false);

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  // What actually leaves the balance is amount + WHT, since the
  // withheld portion still settles the vendor's invoice.
  const totalSettled = Number(form.amount || 0) + Number(form.wht_amount || 0);

  const save = async () => {
    if (!(Number(form.amount) > 0)) return notify('error', 'Amount must be more than 0');
    if (!form.payment_date)         return notify('error', 'Payment date is required');

    setSaving(true);
    try {
      await axios.post(`${API}/tape/vendors/payments`, {
        vendor_id: vendor.id,
        ...form,
        wht_amount: form.wht_amount || 0,
      }, { headers });
      notify('success', 'Payment recorded');
      onSaved();
      onClose();
    } catch (err) {
      notify('error', err.response?.data?.message || 'Could not save payment');
    } finally {
      setSaving(false);
    }
  };

  return (
    <WindowModal
      id={`payment-${vendor.id}`}
      title="Record payment"
      subtitle={`${vendor.name} · outstanding ${money(vendor.balance)}`}
      onClose={onClose}
    >
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: '16px', marginBottom: '18px',
      }}>
        <Field label="Payment date">
          <TextInput
            type="date"
            value={form.payment_date}
            onChange={e => setForm({ ...form, payment_date: e.target.value })}
          />
        </Field>

        <Field label="Amount">
          <TextInput
            type="number" step="0.01"
            value={form.amount}
            onChange={e => setForm({ ...form, amount: e.target.value })}
            placeholder="0"
          />
        </Field>

        <Field label="Withholding tax (optional)">
          <TextInput
            type="number" step="0.01"
            value={form.wht_amount}
            onChange={e => setForm({ ...form, wht_amount: e.target.value })}
            placeholder="0"
          />
        </Field>

        <Field label="Method">
          <select
            style={inputStyle}
            value={form.method}
            onChange={e => setForm({ ...form, method: e.target.value })}
          >
            <option value="cash">Cash</option>
            <option value="bank">Bank transfer</option>
            <option value="cheque">Cheque</option>
          </select>
        </Field>

        <Field label="Voucher no.">
          <TextInput
            value={form.ref_no}
            onChange={e => setForm({ ...form, ref_no: e.target.value })}
            placeholder="Optional"
          />
        </Field>

        <Field label="Against bill no.">
          <TextInput
            value={form.against_no}
            onChange={e => setForm({ ...form, against_no: e.target.value })}
            placeholder="Optional"
          />
        </Field>
      </div>

      <Field label="Notes">
        <TextInput
          value={form.notes}
          onChange={e => setForm({ ...form, notes: e.target.value })}
          placeholder="Optional"
        />
      </Field>

      {totalSettled > 0 && (
        <div style={{
          marginTop: '18px', padding: '13px 16px', borderRadius: '8px',
          background: C.borderSoft, fontSize: '14px', color: C.textSoft,
        }}>
          Settles <strong style={{ color: C.text }}>{money(totalSettled)}</strong> against the balance
          {Number(form.wht_amount) > 0 && ' (including withholding tax)'}
        </div>
      )}

      <div style={{ display: 'flex', gap: '10px', marginTop: '22px' }}>
        <Btn onClick={save} disabled={saving}>
          <Check size={16} /> {saving ? 'Saving…' : 'Save payment'}
        </Btn>
        <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
      </div>
    </WindowModal>
  );
};


// ============================================================
// E. LEDGER MODAL
// ============================================================

const LedgerModal = ({ vendor, onClose }) => {
  const [fromDate, setFromDate] = useState(DEFAULT_FROM_DATE);
  const [toDate, setToDate]     = useState(today());
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (fromDate) params.append('from_date', fromDate);
      if (toDate)   params.append('to_date', toDate);

      const res = await axios.get(
        `${API}/tape/vendors/${vendor.id}/ledger?${params.toString()}`,
        { headers }
      );
      setData(res.data.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, vendor.id]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const cell = { padding: '9px 12px', fontSize: '13px', borderTop: `1px solid ${C.borderSoft}` };
  const head = { ...cell, background: C.rowAlt, fontWeight: '600', color: C.textSoft, fontSize: '11px', textTransform: 'uppercase' };

  return (
    <WindowModal
      id={`ledger-${vendor.id}`}
      title="Vendor ledger"
      subtitle={vendor.name}
      onClose={onClose}
      width="860px"
    >
      {/* Date range */}
      <div style={{ display: 'flex', gap: '14px', marginBottom: '20px', flexWrap: 'wrap' }}>
        <div style={{ minWidth: '150px' }}>
          <Field label="From">
            <TextInput type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} />
          </Field>
        </div>
        <div style={{ minWidth: '150px' }}>
          <Field label="To">
            <TextInput type="date" value={toDate} onChange={e => setToDate(e.target.value)} />
          </Field>
        </div>
      </div>

      {loading && <p style={{ color: C.textSoft }}>Loading…</p>}

      {!loading && data && (
        <>
          {/* Summary strip */}
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
            gap: '12px', marginBottom: '20px',
          }}>
            {[
              ['Opening',  `${money(Math.abs(data.opening_balance))} ${data.opening_suffix}`, C.textSoft],
              ['Purchases', money(data.total_credit), C.text],
              ['Payments',  money(data.total_debit),  C.text],
              ['Closing',  `${money(Math.abs(data.closing_balance))} ${data.closing_suffix}`,
                            data.closing_balance > 0 ? C.danger : C.success],
            ].map(([label, value, color]) => (
              <div key={label} style={{
                background: C.rowAlt, borderRadius: '8px', padding: '12px 14px',
              }}>
                <div style={{ fontSize: '11px', color: C.textMuted, fontWeight: '600', textTransform: 'uppercase' }}>
                  {label}
                </div>
                <div style={{ fontSize: '16px', fontWeight: '700', color, marginTop: '3px' }}>
                  {value}
                </div>
              </div>
            ))}
          </div>

          {/* Transactions */}
          <div style={{ overflowX: 'auto', border: `1px solid ${C.border}`, borderRadius: '8px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ ...head, textAlign: 'left' }}>Date</th>
                  <th style={{ ...head, textAlign: 'left' }}>Narration</th>
                  <th style={{ ...head, textAlign: 'right' }}>Debit</th>
                  <th style={{ ...head, textAlign: 'right' }}>Credit</th>
                  <th style={{ ...head, textAlign: 'right' }}>Balance</th>
                </tr>
              </thead>
              <tbody>
                {data.transactions.map((t, i) => (
                  <tr key={i}>
                    <td style={{ ...cell, whiteSpace: 'nowrap', color: C.textSoft }}>
                      {new Date(t.date).toLocaleDateString('en-GB')}
                    </td>
                    <td style={cell}>{t.narration}</td>
                    <td style={{ ...cell, textAlign: 'right', color: t.debit ? C.success : C.textMuted }}>
                      {t.debit ? money(t.debit) : '—'}
                    </td>
                    <td style={{ ...cell, textAlign: 'right', color: t.credit ? C.danger : C.textMuted }}>
                      {t.credit ? money(t.credit) : '—'}
                    </td>
                    <td style={{ ...cell, textAlign: 'right', fontWeight: '600' }}>
                      {money(Math.abs(t.balance))} {t.balance >= 0 ? 'CR' : 'DR'}
                    </td>
                  </tr>
                ))}
                {data.transactions.length === 0 && (
                  <tr>
                    <td colSpan="5" style={{ ...cell, textAlign: 'center', color: C.textMuted, padding: '32px' }}>
                      No transactions in this period
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {!loading && !data && (
        <p style={{ color: C.danger }}>Could not load the ledger.</p>
      )}
    </WindowModal>
  );
};


// ============================================================
// F. MAIN COMPONENT
// ============================================================

const VendorsTab = () => {
  const [vendors, setVendors]         = useState([]);
  const [vendorTypes, setVendorTypes] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [form, setForm]               = useState(null);
  const [saving, setSaving]           = useState(false);
  const [payingVendor, setPayingVendor]   = useState(null);
  const [ledgerVendor, setLedgerVendor]   = useState(null);
  const [banner, setBanner]           = useState({ type: '', message: '' });

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const notify = (type, message) => {
    setBanner({ type, message });
    if (type === 'success') setTimeout(() => setBanner({ type: '', message: '' }), 3000);
  };

  const load = useCallback(async () => {
    try {
      const [v, t] = await Promise.all([
        axios.get(`${API}/tape/vendors`, { headers }),
        axios.get(`${API}/tape/vendors/types`, { headers }),
      ]);
      setVendors(v.data.data || []);
      setVendorTypes((t.data.data || []).filter(x => x.is_active));
    } catch {
      notify('error', 'Could not load vendors. Is the backend running?');
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const openEdit = (row) => setForm({
    ...emptyVendor,
    id: row.id,
    name: row.name || '',
    vendor_type_id: row.vendor_type_id ? String(row.vendor_type_id) : '',
    contact_person: row.contact_person || '',
    phone: row.phone || '',
    email: row.email || '',
    address: row.address || '',
    ntn: row.ntn || '',
    strn: row.strn || '',
    payment_terms_days: row.payment_terms_days ?? 30,
    is_active: row.is_active,
  });

  const save = async () => {
    const f = form;
    if (!f.name.trim()) return notify('error', 'Vendor name is required');

    setSaving(true);
    try {
      // Create the new vendor type first if one was typed
      let typeId = f.vendor_type_id;
      if (f.isNewType && f.newType.trim()) {
        const res = await axios.post(`${API}/tape/vendors/types`,
          { name: f.newType.trim() }, { headers });
        typeId = res.data.data.id;
      }

      const payload = {
        name: f.name,
        vendor_type_id: typeId || null,
        contact_person: f.contact_person,
        phone: f.phone,
        email: f.email,
        address: f.address,
        ntn: f.ntn,
        strn: f.strn,
        payment_terms_days: f.payment_terms_days,
        is_active: f.is_active,
      };

      if (f.id) {
        await axios.put(`${API}/tape/vendors/${f.id}`, payload, { headers });
        notify('success', 'Vendor updated');
      } else {
        await axios.post(`${API}/tape/vendors`, payload, { headers });
        notify('success', 'Vendor added');
      }
      setForm(null);
      load();
    } catch (err) {
      notify('error', err.response?.data?.message || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete "${row.name}"?`)) return;
    try {
      await axios.delete(`${API}/tape/vendors/${row.id}`, { headers });
      notify('success', 'Vendor deleted');
      load();
    } catch (err) {
      notify('error', err.response?.data?.message || 'Could not delete');
    }
  };

  // Headline numbers across all vendors
  const totalOwed = vendors.reduce((sum, v) => sum + Math.max(0, Number(v.balance || 0)), 0);
  const owingCount = vendors.filter(v => Number(v.balance) > 0).length;

  return (
    <div>
      <Banner
        type={banner.type}
        message={banner.message}
        onClose={() => setBanner({ type: '', message: '' })}
      />

      {/* Summary cards */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
        gap: '14px', marginBottom: '20px',
      }}>
        {[
          ['Vendors', vendors.length, C.text],
          ['Total payable', money(totalOwed), totalOwed > 0 ? C.danger : C.success],
          ['With balance', owingCount, C.text],
        ].map(([label, value, color]) => (
          <div key={label} style={{
            background: C.card, border: `1px solid ${C.border}`,
            borderRadius: '10px', padding: '16px 18px',
          }}>
            <div style={{ fontSize: '11px', color: C.textMuted, fontWeight: '600', textTransform: 'uppercase' }}>
              {label}
            </div>
            <div style={{ fontSize: '22px', fontWeight: '700', color, marginTop: '4px' }}>
              {value}
            </div>
          </div>
        ))}
      </div>

      <div style={{
        background: C.card, border: `1px solid ${C.border}`,
        borderRadius: '12px', overflow: 'hidden',
      }}>
        <div style={{
          padding: '18px 22px', borderBottom: `1px solid ${C.borderSoft}`,
          display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap',
        }}>
          <div style={{ flex: 1, minWidth: '200px' }}>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: C.text }}>
              Vendors
            </h3>
            <p style={{ margin: '3px 0 0', fontSize: '13px', color: C.textSoft }}>
              Balance is calculated from purchases minus payments.
            </p>
          </div>
          {!form && (
            <Btn onClick={() => setForm(emptyVendor)}>
              <Plus size={16} /> Add vendor
            </Btn>
          )}
        </div>

        {form && (
          <VendorForm
            form={form}
            setForm={setForm}
            vendorTypes={vendorTypes}
            onSave={save}
            onCancel={() => setForm(null)}
            saving={saving}
          />
        )}

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={thStyle}>Vendor</th>
                <th style={thStyle}>Type</th>
                <th style={thStyle}>Phone</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Terms</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Balance</th>
                <th style={thStyle}>Status</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan="7" style={{ ...tdStyle, textAlign: 'center', color: C.textMuted, padding: '36px' }}>
                    Loading…
                  </td>
                </tr>
              )}

              {!loading && vendors.map(row => {
                const bal = Number(row.balance || 0);
                return (
                  <tr key={row.id}>
                    <td style={{ ...tdStyle, fontWeight: '600' }}>
                      {row.name}
                      {row.contact_person && (
                        <div style={{ fontSize: '12px', color: C.textMuted, fontWeight: '400' }}>
                          {row.contact_person}
                        </div>
                      )}
                    </td>
                    <td style={tdStyle}>{row.vendor_type_name || '—'}</td>
                    <td style={tdStyle}>{row.phone || '—'}</td>
                    <td style={{ ...tdStyle, textAlign: 'right', color: C.textSoft }}>
                      {row.payment_terms_days} d
                    </td>
                    <td style={{
                      ...tdStyle, textAlign: 'right', fontWeight: '700',
                      color: bal > 0 ? C.danger : (bal < 0 ? C.info : C.textMuted),
                    }}>
                      {bal === 0 ? '—' : `${money(Math.abs(bal))} ${bal > 0 ? 'CR' : 'DR'}`}
                    </td>
                    <td style={tdStyle}><StatusPill active={row.is_active} /></td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>
                      <div style={{ display: 'inline-flex', gap: '7px' }}>
                        <IconBtn title="Ledger" onClick={() => setLedgerVendor(row)}>
                          <FileText size={15} />
                        </IconBtn>
                        <IconBtn title="Record payment" onClick={() => setPayingVendor(row)}>
                          <Wallet size={15} />
                        </IconBtn>
                        <IconBtn title="Edit" onClick={() => openEdit(row)}>
                          <Pencil size={15} />
                        </IconBtn>
                        <IconBtn danger title="Delete" onClick={() => remove(row)}>
                          <Trash2 size={15} />
                        </IconBtn>
                      </div>
                    </td>
                  </tr>
                );
              })}

              {!loading && vendors.length === 0 && (
                <tr>
                  <td colSpan="7" style={{ ...tdStyle, textAlign: 'center', color: C.textMuted, padding: '40px' }}>
                    No vendors yet. Click "Add vendor" to create the first one.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {payingVendor && (
        <PaymentModal
          vendor={payingVendor}
          onClose={() => setPayingVendor(null)}
          onSaved={load}
          notify={notify}
        />
      )}

      {ledgerVendor && (
        <LedgerModal
          vendor={ledgerVendor}
          onClose={() => setLedgerVendor(null)}
        />
      )}
    </div>
  );
};

export default VendorsTab;